import React, { useState, useCallback, useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import { FORCED_AUTHENTICATION, STORAGE_KEY_STUDENT_PROXY, STORAGE_KEY_EXTERNAL_USER_KEYS } from "../util/constants";
import useAmplifyHub from "../Common/hooks/useAmplifyHub";
import { useIdleTimer } from "react-idle-timer";
import { useNavigate } from "react-router-dom";
import { getExternalUserInfo } from "./actions";

const TIMEOUT = 60000 * (parseInt(process.env.REACT_APP_TIMEOUT) || 120);

// Authentication states enum
const AUTH_STATE = {
  IDLE: 'idle',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
  FAILED: 'failed'
};

function withAuthentication(Component) {
  function _withAuthentication(props = {}) {
    const { status, ...rest } = props
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const externalUserStorageString = sessionStorage.getItem(STORAGE_KEY_EXTERNAL_USER_KEYS);
    const isExternalUser = !!externalUserStorageString
    const [authState, setAuthState] = useState(AUTH_STATE.IDLE);
    const { handleSignOut: amplifySignOut, handleSignIn: amplifySignIn } = useAmplifyHub();
    const authorized = FORCED_AUTHENTICATION ? status === 'success' : true

    const handleExternalUserAuth = useCallback(async (externalUserStorage, redirectPath) => {
      if (!externalUserStorage?.ukey || !externalUserStorage?.akey) {
        sessionStorage.removeItem(STORAGE_KEY_EXTERNAL_USER_KEYS)
        navigate('/login')
        return false
      }

      try {
        const user = await dispatch(getExternalUserInfo(externalUserStorage.ukey, externalUserStorage.akey))
        
        if (!user) {
          throw new Error('No user data received')
        }
        
        navigate(redirectPath || '/tasks')
        return true
      } catch (error) {
        sessionStorage.removeItem(STORAGE_KEY_EXTERNAL_USER_KEYS)
        navigate('/login')
        return false
      }
    }, [navigate, dispatch])
    
    
    const handleSignIn = useCallback(async (redirectPath) => {
      if (authState === AUTH_STATE.IN_PROGRESS) {
        return;
      }
      
      // Check if we just had a failed authentication attempt
      const authFailed = sessionStorage.getItem('AUTH_FAILED')
      if (authFailed) {
        sessionStorage.removeItem('AUTH_FAILED')
        return
      }
    
      try {
        setAuthState(AUTH_STATE.IN_PROGRESS)
        
        if (isExternalUser) {
          if (externalUserStorageString) {
            try {
              const externalUserStorage = JSON.parse(externalUserStorageString);
              const success = await handleExternalUserAuth(externalUserStorage, redirectPath)
              setAuthState(success ? AUTH_STATE.COMPLETED : AUTH_STATE.FAILED)
            } catch (error) {
              console.error('Error with external user:', error);
              sessionStorage.removeItem(STORAGE_KEY_EXTERNAL_USER_KEYS)
              navigate('/login')
              setAuthState(AUTH_STATE.FAILED)
            }
          } else {
            navigate('/login')
            setAuthState(AUTH_STATE.IDLE)
          }      
        } else {
          await amplifySignIn(redirectPath)
          setAuthState(AUTH_STATE.COMPLETED)
        }
      } catch (error) {
        sessionStorage.removeItem(STORAGE_KEY_EXTERNAL_USER_KEYS)
        navigate('/login')
        setAuthState(AUTH_STATE.FAILED)
      }
    }, [authState, isExternalUser, externalUserStorageString, handleExternalUserAuth, amplifySignIn, navigate])
    
    // Cleanup effect for auth state
    useEffect(() => {
      return () => {
        setAuthState(AUTH_STATE.IDLE)
      }
    }, [])

    const handleOnIdle = (event) => {
      console.log("user is idle", event);
      console.log("last active", getLastActiveTime());
      navigate('/logout')
    };

    const { getLastActiveTime } = useIdleTimer({
      timeout: TIMEOUT,
      onIdle: handleOnIdle,
    });

    const handleSignOut = useCallback(() => {
      sessionStorage.removeItem(STORAGE_KEY_EXTERNAL_USER_KEYS)
      sessionStorage.removeItem(STORAGE_KEY_STUDENT_PROXY);
      amplifySignOut()
    }, [amplifySignOut])
    
    return <Component 
      handleSignIn={handleSignIn}
      handleSignOut={handleSignOut} 
      authorized={authorized} 
      isAuthenticating={authState === AUTH_STATE.IN_PROGRESS}
      {...rest} 
    />
  }

  const mapStateToProps = (state) => ({
    status: state.authentication?.status,
    authentication: state.authentication,
  });

  return connect(mapStateToProps, undefined)(_withAuthentication);
}

export default withAuthentication;




